import React, { useEffect, useState } from "react";
import {
    Box,
    Card,
    CardContent,
    Typography,
    CircularProgress,
    MenuItem,
    Select,
    FormControl,
} from "@mui/material";
import {
    ResponsiveContainer,
    BarChart,
    Bar,
    LineChart,
    Line,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
} from "recharts";
import { API_ENDPOINTS, getData } from "../../services/apiService.js";
import { useNavigate } from "react-router-dom";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import DirectionsCarIcon from "@mui/icons-material/DirectionsCar";
import CurrencyRupeeIcon from "@mui/icons-material/CurrencyRupee";
import PeopleIcon from "@mui/icons-material/People";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import LoadingSpinner from "../LoadingContext.js";

export default function Dashboard() {
    const [summary, setSummary] = useState(null);
    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();

    // ---------------- STATES FOR BAR CHART ----------------
    const [barRange, setBarRange] = useState("month"); // "today" | "week" | "month" | "year"
    const [barChartData, setBarChartData] = useState([]);
    const [barChartLoading, setBarChartLoading] = useState(false);

    // ---------------- STATES FOR LINE CHART ----------------
    const [lineRange, setLineRange] = useState("month");
    const [revenueChartData, setRevenueChartData] = useState([]);
    const [lineChartLoading, setLineChartLoading] = useState(false);

    const titleMap = {
        today: "Today's Trips",
        week: "Weekly Trips",
        month: "Monthly Trips",
        year: "Yearly Trips",
    };

    const titleLineMap = {
        today: "Today's Total Revenue",
        week: "Weekly Total Revenue",
        month: "Monthly Total Revenue",
        year: "Yearly Total Revenue",
    };

    // ------------------- DASHBOARD SUMMARY -------------------
    useEffect(() => {
        const fetchSummary = async () => {
            setLoading(true);
            const start = Date.now();
            try {
                const res = await getData(API_ENDPOINTS.dashboardSummary);
                setSummary(res.result);
            } catch (err) {
                console.error("Failed to load dashboard summary", err);
            } finally {
                const elapsed = Date.now() - start;
                const remaining = Math.max(0, 3000 - elapsed);
                setTimeout(() => setLoading(false), remaining);
            }
        };
        fetchSummary();
    }, []);

    // ------------------- FETCH TRIP CHART DATA -------------------
    const fetchTripChartData = async (range) => {
        setBarChartLoading(true);
        try {
            const params = {};
            params.filter =
                range === "today"
                    ? "today"
                    : range === "week"
                        ? "this_week"
                        : range === "month"
                            ? "this_month"
                            : "this_year";

            const res = await getData(API_ENDPOINTS.tripAnalytics, params);
            setBarChartData(res.result.chartData);
        } catch (e) {
            console.error("Failed to load trip chart data", e);
            setBarChartData([]);
        } finally {
            setBarChartLoading(false);
        }
    };

    useEffect(() => {
        fetchTripChartData(barRange);
    }, [barRange]);

    // ------------------- FETCH REVENUE CHART DATA -------------------
    const fetchRevenueChartData = async (range) => {
        setLineChartLoading(true);
        try {
            const params = {};
            params.filter =
                range === "today"
                    ? "today"
                    : range === "week"
                        ? "this_week"
                        : range === "month"
                            ? "this_month"
                            : "this_year";

            const res = await getData(API_ENDPOINTS.getRevenueChart, params);
            setRevenueChartData(res.result.chartData);
        } catch (e) {
            console.error("Failed to load revenue chart data", e);
            setRevenueChartData([]);
        } finally {
            setLineChartLoading(false);
        }
    };

    useEffect(() => {
        fetchRevenueChartData(lineRange);
    }, [lineRange]);

    // ------------------- CARDS -------------------
    const cards = [
        {
            title: "Total Trips",
            value: summary ? summary.totalTrips.toLocaleString() : "--",
            icon: <LocalShippingIcon fontSize="large" htmlColor="#1976d2" />,
            bg: "#E3F2FD",
            route: "/app/trips",
        },
        {
            title: "Active Trucks",
            value: summary ? summary.totalTrucks.toString() : "--",
            icon: <DirectionsCarIcon fontSize="large" htmlColor="#2e7d32" />,
            bg: "#E8F5E9",
            route: "/app/trucks",
        },
        {
            title: "Total Revenue",
            value: summary ? summary.totalRevenue.toLocaleString() : "--",
            icon: <CurrencyRupeeIcon fontSize="large" htmlColor="#6a1b9a" />,
            bg: "#F3E5F5",
            route: "/app/payments",
        },
        {
            title: "Platform Revenue",
            value: revenueChartData.length && summary
                ? `₹${summary.platformRevenue.toLocaleString()}`
                : "--",
            icon: <CurrencyRupeeIcon fontSize="large" htmlColor="#6a1b9a" />,
            bg: "#F3E5F5",
            route: "/app/payments",
        },
        {
            title: "Active Drivers",
            value: summary ? summary.totalDrivers.toString() : "--",
            icon: <PeopleIcon fontSize="large" htmlColor="#f57c00" />,
            bg: "#FFF3E0",
            route: "/app/drivers",
        },
        {
            title: "Cities Covered",
            value: summary ? summary.totalCities.toString() : "--",
            icon: <LocationOnIcon fontSize="large" htmlColor="#0288d1" />,
            bg: "#E1F5FE",
            route: "/app/delivery-zones",
        },
    ];

    const formatTripData = (data) =>
        data.map((d) => ({
            date: new Date(d.date).toLocaleDateString("en-GB", {
                month: "short",
                day: "numeric",
            }),
            trips: d.tripCount,
        }));

    const formatRevenueData = (data) =>
        data.map((d) => ({
            date: new Date(d.date).toLocaleDateString("en-GB", {
                month: "short",
                day: "numeric",
            }),
            totalRevenue: d.totalRevenue,
            cod: d.cod,
            wallet: d.wallet,
            razorpay: d.razorpay,
        }));

    return (
        <Box padding={2}>
            <LoadingSpinner isLoading={loading} />

            <Box display="flex" justifyContent="space-between" mb={3}>
                <Box>
                    <Typography variant="h5" fontWeight={600}>
                        Dashboard
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Welcome back! Here's what's happening with your fleet today.
                    </Typography>
                </Box>
                <Typography variant="caption" color="text.secondary">
                    Last updated:{" "}
                    {summary ? new Date(summary.lastUpdated).toLocaleString() : "loading..."}
                </Typography>
            </Box>

            {!summary && loading ? (
                <Box textAlign="center" py={4}>
                    <CircularProgress />
                </Box>
            ) : (
                <>
                    {/* SUMMARY CARDS */}
                    <Box display="flex" flexWrap="wrap" gap={2} mb={4}>
                        {cards.map((c) => (
                            <Card
                                key={c.title}
                                sx={{
                                    flex: "0 0 calc(25% - 16px)",
                                    minWidth: 220,
                                    borderRadius: 3,
                                    cursor: "pointer",
                                    transition: "transform 0.2s",
                                    "&:hover": { transform: "scale(1.02)" },
                                }}
                                onClick={() => navigate(c.route)}
                            >
                                <CardContent sx={{ height: "100%" }}>
                                    <Box
                                        display="flex"
                                        justifyContent="space-between"
                                        alignItems="flex-start"
                                        height="100%"
                                    >
                                        <Box>
                                            <Typography fontWeight={500} color="#7c7878ff">
                                                {c.title}
                                            </Typography>
                                            <Typography variant="h5" fontWeight={700} mt={1}>
                                                {c.value}
                                            </Typography>
                                        </Box>
                                        <Box
                                            sx={{
                                                width: 35,
                                                height: 35,
                                                borderRadius: 2,
                                                background: c.bg,
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                padding: 3,
                                            }}
                                        >
                                            {c.icon}
                                        </Box>
                                    </Box>
                                </CardContent>
                            </Card>
                        ))}
                    </Box>

                    {/* ---------------- BAR AND LINE CHARTS ---------------- */}
                    <Box display="flex" gap={2} flexDirection="row">
                        {/* BAR CHART - Trips */}
                        <Card sx={{ borderRadius: 3, flex: "1 1 50%" }}>
                            <CardContent>
                                <Box
                                    display="flex"
                                    justifyContent="space-between"
                                    alignItems="center"
                                    mb={2}
                                >
                                    <Typography variant="h6">{titleMap[barRange]}</Typography>
                                    <FormControl size="small">
                                        <Select
                                            value={barRange}
                                            onChange={(e) => setBarRange(e.target.value)}
                                        >
                                            <MenuItem value="today">Today</MenuItem>
                                            <MenuItem value="week">This Week</MenuItem>
                                            <MenuItem value="month">This Month</MenuItem>
                                            <MenuItem value="year">This Year</MenuItem>
                                        </Select>
                                    </FormControl>
                                </Box>
                                <Box
                                    sx={{ height: 300 }}
                                    display="flex"
                                    justifyContent="center"
                                    alignItems="center"
                                >
                                    {barChartLoading ? (
                                        <CircularProgress />
                                    ) : barChartData.length === 0 ? (
                                        <Typography>No data found</Typography>
                                    ) : (
                                        <ResponsiveContainer width="100%" height="100%">
                                            <BarChart data={formatTripData(barChartData)}>
                                                <CartesianGrid strokeDasharray="3 3" />
                                                <XAxis dataKey="date" />
                                                <YAxis />
                                                <Tooltip />
                                                <Bar
                                                    dataKey="trips"
                                                    fill="#1976d2"
                                                    radius={[4, 4, 0, 0]}
                                                />
                                            </BarChart>
                                        </ResponsiveContainer>
                                    )}
                                </Box>
                            </CardContent>
                        </Card>

                        {/* LINE CHART - Revenue */}
                        <Card sx={{ borderRadius: 3, flex: "1 1 50%" }}>
                            <CardContent>
                                <Box
                                    display="flex"
                                    justifyContent="space-between"
                                    alignItems="center"
                                    mb={2}
                                >
                                    <Typography variant="h6">{titleLineMap[lineRange]}</Typography>
                                    <FormControl size="small">
                                        <Select
                                            value={lineRange}
                                            onChange={(e) => setLineRange(e.target.value)}
                                        >
                                            <MenuItem value="today">Today</MenuItem>
                                            <MenuItem value="week">This Week</MenuItem>
                                            <MenuItem value="month">This Month</MenuItem>
                                            <MenuItem value="year">This Year</MenuItem>
                                        </Select>
                                    </FormControl>
                                </Box>
                                <Box
                                    sx={{ height: 300 }}
                                    display="flex"
                                    justifyContent="center"
                                    alignItems="center"
                                >
                                    {lineChartLoading ? (
                                        <CircularProgress />
                                    ) : revenueChartData.length === 0 ? (
                                        <Typography>No data found</Typography>
                                    ) : (
                                        <ResponsiveContainer width="100%" height="100%">
                                            <LineChart data={formatRevenueData(revenueChartData)}>
                                                <CartesianGrid strokeDasharray="3 3" />
                                                <XAxis dataKey="date" />
                                                <YAxis />
                                                <Tooltip
                                                    formatter={(value) =>
                                                        `₹${value.toLocaleString()}`
                                                    }
                                                />
                                                <Legend />
                                                <Line
                                                    type="monotone"
                                                    dataKey="totalRevenue"
                                                    stroke="#6a1b9a"
                                                    strokeWidth={2}
                                                    name="Total Revenue"
                                                />
                                                <Line
                                                    type="monotone"
                                                    dataKey="cod"
                                                    stroke="#ff5722"
                                                    strokeWidth={2}
                                                    name="COD"
                                                />
                                                <Line
                                                    type="monotone"
                                                    dataKey="wallet"
                                                    stroke="#1976d2"
                                                    strokeWidth={2}
                                                    name="Wallet"
                                                />
                                                <Line
                                                    type="monotone"
                                                    dataKey="razorpay"
                                                    stroke="#388e3c"
                                                    strokeWidth={2}
                                                    name="Razorpay"
                                                />
                                            </LineChart>
                                        </ResponsiveContainer>
                                    )}
                                </Box>
                            </CardContent>
                        </Card>
                    </Box>
                </>
            )}
        </Box>
    );
}
